/**
 * Convert any YouTube / Vimeo URL (or a raw youtu.be short link with tracking params)
 * into a clean embeddable iframe URL. Returns null if the URL cannot be embedded.
 *
 * Supported YouTube formats:
 *   https://youtu.be/VIDEO_ID
 *   https://youtu.be/VIDEO_ID?si=...
 *   https://www.youtube.com/watch?v=VIDEO_ID
 *   https://youtube.com/watch?v=VIDEO_ID&list=...
 *   https://www.youtube.com/embed/VIDEO_ID
 *   https://www.youtube.com/v/VIDEO_ID
 *   https://www.youtube.com/shorts/VIDEO_ID
 *
 * Tracking parameters (si, feature, list, t, start, etc.) are intentionally dropped.
 */

const YT_PARAMS = "autoplay=1&rel=0&playsinline=1&modestbranding=1&enablejsapi=1";

function youTubeId(url) {
  if (!url || typeof url !== "string") return null;
  const patterns = [
    /youtu\.be\/([A-Za-z0-9_-]{11})/,
    /[?&]v=([A-Za-z0-9_-]{11})/,
    /youtube\.com\/embed\/([A-Za-z0-9_-]{11})/,
    /youtube\.com\/(?:v|shorts|live)\/([A-Za-z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
}

function vimeoId(url) {
  if (!url || typeof url !== "string") return null;
  const m = url.match(/vimeo\.com\/(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/)?(\d+)/);
  return m ? m[1] : null;
}

export function toYouTubeEmbed(url) {
  const id = youTubeId(url);
  return id ? `https://www.youtube.com/embed/${id}?${YT_PARAMS}` : null;
}

export function toVimeoEmbed(url) {
  const id = vimeoId(url);
  return id ? `https://player.vimeo.com/video/${id}?autoplay=1&dnt=1` : null;
}

/** Returns an embeddable iframe URL or null. */
export function toEmbedUrl(url) {
  return toYouTubeEmbed(url) || toVimeoEmbed(url) || null;
}

/** Returns 'youtube' | 'vimeo' | null */
export function detectProvider(url) {
  if (youTubeId(url)) return "youtube";
  if (vimeoId(url)) return "vimeo";
  return null;
}

/** Returns a clean watch URL pointing back to the platform (for "Open on YouTube" fallbacks). */
export function toWatchUrl(url) {
  const yt = youTubeId(url);
  if (yt) return `https://www.youtube.com/watch?v=${yt}`;
  const vm = vimeoId(url);
  if (vm) return `https://vimeo.com/${vm}`;
  return url || null;
}
